import { defineModule, localActionContext, localGetterContext } from 'direct-vuex'

import { cloneRaw } from '@/util/util'

import { HaftarahMelodyType, PronunciationType, Query, SentenceGroupMelodyType, TorahMelodyType } from '../../types/types'
import HaftarahMelodiesQuery from '../queries/HaftarahMelodiesQuery.graphql'
import PronunciationsQuery from '../queries/PronunciationsQuery.graphql'
import SentenceGroupMelodiesQuery from '../queries/SentenceGroupMelodiesQuery.graphql'
import TorahMelodiesQuery from '../queries/TorahMelodiesQuery.graphql'
import { useApollo } from '../util/apolloClient'
const { apolloClient } = useApollo()

export interface StyleState {
  torahMelodies: Array<TorahMelodyType>
  haftarahMelodies: Array<HaftarahMelodyType>
  sentenceGroupMelodies: Array<SentenceGroupMelodyType>
  pronunciations: Array<PronunciationType>
}

const resetState = (): StyleState => {
  return {
    haftarahMelodies: [],
    pronunciations: [],
    sentenceGroupMelodies: [],
    torahMelodies: [],
  }
}

const style = defineModule({
  actions: {
    async fetchStyles(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = styleActionContext(context)
      await Promise.all([
        apolloClient
          .query<Pick<Query, 'torahMelodies'>>({
            fetchPolicy: 'network-only',
            query: TorahMelodiesQuery,
          })
          .then((response) => {
            commit.setTorahMelodies((response.data?.torahMelodies?.filter(Boolean) as TorahMelodyType[]) ?? [])
          }),

        apolloClient
          .query<Pick<Query, 'haftarahMelodies'>>({
            fetchPolicy: 'network-only',
            query: HaftarahMelodiesQuery,
          })
          .then((response) => {
            commit.setHaftarahMelodies((response.data?.haftarahMelodies?.filter(Boolean) as HaftarahMelodyType[]) ?? [])
          }),

        apolloClient
          .query<Pick<Query, 'sentenceGroupMelodies'>>({
            fetchPolicy: 'network-only',
            query: SentenceGroupMelodiesQuery,
          })
          .then((response) => {
            commit.setSentenceGroupMelodies((response.data?.sentenceGroupMelodies?.filter(Boolean) as SentenceGroupMelodyType[]) ?? [])
          }),

        apolloClient
          .query<Pick<Query, 'pronunciations'>>({
            fetchPolicy: 'network-only',
            query: PronunciationsQuery,
          })
          .then((response) => {
            commit.setPronunciations((response.data?.pronunciations?.filter(Boolean) as PronunciationType[]) ?? [])
          }),
      ])
    },

    reset(context) {
      // eslint-disable-next-line no-use-before-define
      const { commit } = styleActionContext(context)
      commit.reset()
    },
  },
  getters: {
    haftarahMelodies(...args): Array<HaftarahMelodyType> {
      // eslint-disable-next-line no-use-before-define
      const { state } = styleGetterContext(args)
      return state.haftarahMelodies
    },

    pronunciations(...args): Array<PronunciationType> {
      // eslint-disable-next-line no-use-before-define
      const { state } = styleGetterContext(args)
      return state.pronunciations
    },

    sentenceGroupMelodies(...args): Array<SentenceGroupMelodyType> {
      // eslint-disable-next-line no-use-before-define
      const { state } = styleGetterContext(args)
      return state.sentenceGroupMelodies
    },

    torahMelodies(...args): Array<TorahMelodyType> {
      // eslint-disable-next-line no-use-before-define
      const { state } = styleGetterContext(args)
      return state.torahMelodies
    },
  },

  mutations: {
    reset(state: StyleState) {
      Object.assign(state, { ...resetState() })
    },

    setHaftarahMelodies(state: StyleState, haftarahMelodies: Array<HaftarahMelodyType>) {
      state.haftarahMelodies = cloneRaw(haftarahMelodies)
    },

    setPronunciations(state: StyleState, pronunciations: Array<PronunciationType>) {
      state.pronunciations = cloneRaw(pronunciations)
    },

    setSentenceGroupMelodies(state: StyleState, sentenceGroupMelodies: Array<SentenceGroupMelodyType>) {
      state.sentenceGroupMelodies = cloneRaw(sentenceGroupMelodies)
    },

    setTorahMelodies(state: StyleState, torahMelodies: Array<TorahMelodyType>) {
      state.torahMelodies = cloneRaw(torahMelodies)
    },
  },

  namespaced: true as const,

  state: resetState(),
})

export default style
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const styleGetterContext = (args: [any, any, any, any]) => localGetterContext(args, style)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const styleActionContext = (context: any) => localActionContext(context, style)
